/* eslint-disable react-native/no-inline-styles */
import * as React from "react";
import { useController, RegisterOptions } from "react-hook-form";
import { Keyboard, StyleProp, StyleSheet, TextStyle, useWindowDimensions, View } from "react-native";
import { HelperText, TextInput, TouchableRipple, useTheme } from "react-native-paper";
import AutoComplete, { ItemCompleteProps } from "../../modal/auto-complete/auto-complete";
import GlobalAutoComplete, { createGlobalAutoComplete, globalAutoComplete } from "../../modal/auto-complete/global-auto-complete";
import { testProps } from "app/shared/util/test-props";

interface InputAutoCompleteProps {
  control: any;
  name: string;
  inputProps?: object;
  style?: StyleProp<TextStyle>;
  label: string;
  rules?: Omit<RegisterOptions, "valueAsNumber" | "valueAsDate" | "setValueAs">;
  options: ItemCompleteProps[];
  title: string;
  pending?: boolean;
  onClick?: Function;
  checkSelect?: boolean;
  onDismiss?: Function;
  isShow?: boolean;
  showSearch?: boolean;
  showIcon?: boolean;
  typeSearch?: string;
}

export const InputAutoComplete = (props: InputAutoCompleteProps) => {
  const {
    control,
    name,
    inputProps,
    style = {},
    label,
    rules,
    options,
    title,
    onDismiss = () => {},
    pending,
    onClick,
    checkSelect = false,
    isShow = true,
    showSearch = false,
    typeSearch,
    showIcon = false,
  } = props;
  const {
    field,
    fieldState: { invalid, error },
  } = useController({
    name,
    control,
    rules,
    defaultValue: "",
  });
  const { colors } = useTheme();
  const { width } = useWindowDimensions();

  const [autoCompleteRef] = React.useState<React.RefObject<any>>(createGlobalAutoComplete());
  const [heightInput, setHeightInput] = React.useState<number | undefined>();

  const styles = React.useMemo(
    () =>
      StyleSheet.create({
        inputStyle: {
          flex: 1,
          color: colors.accent,
          backgroundColor: colors.backgroundForm,
          fontWeight: "700",
          textShadowColor: "black",
          height: heightInput,
        },
      }),
    [colors.accent, colors.backgroundForm, heightInput],
  );

  const getTextValue = () => {
    const items = options.filter((option) => {
      return option.value === field.value;
    });
    if (items.length) {
      return items[0].label;
    }
    return field.value;
  };

  const onSelected = (item: ItemCompleteProps) => {
    globalAutoComplete.hide(autoCompleteRef);
    setTimeout(() => {
      onDismiss(item);
    }, 400);
  };

  const onPressItem = () => {
    Keyboard.dismiss();
    if (onClick) onClick();
    console.log("checkSelect: ", checkSelect);
    if (!checkSelect) {
      const content = (
        <AutoComplete
          options={options}
          field={field}
          title={title}
          pending={pending}
          onSelect={onSelected}
          showSearch={showSearch}
          showIcon={showIcon}
          typeSearch={typeSearch}
        />
      );
      globalAutoComplete.show(autoCompleteRef, content);
    }
  };

  React.useEffect(() => {
    setHeightInput(undefined);
  }, [width]);

  return (
    <React.Fragment>
      <TouchableRipple style={{ display: isShow ? "flex" : "none" }} onPress={onPressItem} {...testProps(name)}>
        <View pointerEvents="none">
          <TextInput
            mode={"flat"}
            outlineColor={"transparent"}
            theme={{
              colors: {
                primary: colors.accent,
                text: colors.accent,
              },
            }}
            style={[styles.inputStyle, style]}
            {...inputProps}
            editable={false}
            onKeyPress={(e) => {
              e.nativeEvent.key === "Tab" && e.preventDefault();
            }}
            ref={field.ref}
            value={getTextValue()}
            onChangeText={field.onChange}
            onBlur={field.onBlur}
            label={label}
            error={invalid}
            multiline
            onContentSizeChange={(e) => {
              setHeightInput(e.nativeEvent.contentSize.height);
            }}
            right={<TextInput.Icon size={28} name="chevron-down" onPress={onPressItem} />}
          />
          {invalid && error?.message && (
            <HelperText type="error" visible={invalid}>
              {error?.message}
            </HelperText>
          )}
        </View>
      </TouchableRipple>
      <GlobalAutoComplete ref={autoCompleteRef} />
    </React.Fragment>
  );
};
